import { CarePlanType } from '~clients/models';

// Care Plan Type configuration. Frontend-only, hardcoded (NOT fetched from the API).
// Drives per-plan-type tab visibility and activation requirements. Adding a future
// plan type (e.g. NDIS) requires only a new entry here — no changes to tab components.
//
// The activate endpoint is currently a placeholder (full server-side validation is
// deferred). The finalized activation gate is: an SAH budget (both plan types) plus a
// cultural-safety record (sah_ongoing only). These are the recommended checklist gates
// surfaced client-side until the server logic lands.

export type CarePlanTab = 'overview' | 'needs' | 'goals' | 'service_lines' | 'cultural_safety' | 'reviews';

/** Display labels for care plan tabs (tab nav titles). */
export const CARE_PLAN_TAB_LABELS: Record<CarePlanTab, string> = {
  overview: 'Overview',
  needs: 'Assessed Needs',
  goals: 'Goals',
  service_lines: 'Services & Delivery',
  cultural_safety: 'Cultural Safety',
  reviews: 'Reviews',
};

/** Activation conditions surfaced in the per-type activation checklist. */
export type CarePlanActivationRequirement = 'sah_budget' | 'cultural_safety';

/**
 * Overview-tab activation checklist items. Every item is derived from live plan
 * data (see useActivationChecklist) — none can be manually checked off.
 */
export type CarePlanChecklistItem =
  | 'valid_dates'
  | 'package_schedule_linked'
  | 'sah_budget_linked'
  | 'participant_copy_sent'
  | 'rights_explained'
  | 'service_agreement_signed'
  | 'cultural_safety_completed'
  | 'all_goals_have_service';

export const CARE_PLAN_CHECKLIST_LABELS: Record<CarePlanChecklistItem, string> = {
  valid_dates: 'Valid start & end dates',
  package_schedule_linked: 'Package schedule linked',
  sah_budget_linked: 'Linked to SAH budget',
  participant_copy_sent: 'Participant copy sent',
  rights_explained: 'Statement of Rights explained',
  service_agreement_signed: 'Service agreement signed',
  cultural_safety_completed: 'Cultural safety section completed',
  all_goals_have_service: 'All goals have at least one service',
};

export interface CarePlanTypeConfig {
  /** Human-readable label for the plan type. */
  label: string;
  /** Tabs visible for this plan type, in display order. */
  tabs: CarePlanTab[];
  /** Activation checklist items for this plan type, in display order. */
  checklist: CarePlanChecklistItem[];
  /** Whether a linked SAH budget is required before activation. */
  requiresSahBudget: boolean;
  /** Whether a cultural-safety record is required before activation. */
  requiresCulturalSafety: boolean;
}

export const CARE_PLAN_TYPES = {
  sah_ongoing: {
    label: 'Support at Home (Ongoing)',
    tabs: ['overview', 'needs', 'goals', 'service_lines', 'cultural_safety', 'reviews'],
    checklist: [
      'valid_dates',
      'package_schedule_linked',
      'sah_budget_linked',
      'participant_copy_sent',
      'rights_explained',
      'service_agreement_signed',
      'cultural_safety_completed',
      'all_goals_have_service',
    ],
    requiresSahBudget: true,
    requiresCulturalSafety: true,
  },
  sah_restorative: {
    label: 'Support at Home (Restorative Care)',
    tabs: ['overview', 'goals', 'service_lines', 'reviews'],
    checklist: [
      'valid_dates',
      'package_schedule_linked',
      'sah_budget_linked',
      'participant_copy_sent',
      'all_goals_have_service',
    ],
    requiresSahBudget: true,
    requiresCulturalSafety: false,
  },
} satisfies Record<CarePlanType, CarePlanTypeConfig>;

/** Config for a plan type. */
export const getCarePlanTypeConfig = (planType: CarePlanType): CarePlanTypeConfig => CARE_PLAN_TYPES[planType];

/** Whether a given tab is visible for a plan type. */
export const isCarePlanTabVisible = (planType: CarePlanType, tab: CarePlanTab): boolean =>
  getCarePlanTypeConfig(planType).tabs.includes(tab);

/** Ordered list of activation conditions for a plan type (drives the activation checklist). */
export const getCarePlanActivationRequirements = (planType: CarePlanType): CarePlanActivationRequirement[] => {
  const config = getCarePlanTypeConfig(planType);
  return [
    ...(config.requiresSahBudget ? (['sah_budget'] as const) : []),
    ...(config.requiresCulturalSafety ? (['cultural_safety'] as const) : []),
  ];
};
